import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { SousBienService } from 'app/services/sousBiens/sous-bien.service';
import Swal from 'sweetalert2';

interface SousBien {
  id: number;
  name: string;
  image_url?: string;
  category?: string;
  prix?: number;
  displayOrder?: number;
  orderIndex?: number;
  createdAt?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-list-agence-sous-bien',
  templateUrl: './list-agence-sous-bien.component.html',
  styleUrls: ['./list-agence-sous-bien.component.scss']
})
export class ListAgenceSousBienComponent implements OnInit {
  id: number;
  sousBiens: SousBien[] = [];
  bienParent: any = null;
  isLoading = false;

  constructor(
    private sousBien: SousBienService,
    private route: ActivatedRoute,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const idParam = params.get('id');
      this.id = idParam ? +idParam : 0;

      if (this.id <= 0) {
        Swal.fire({
          title: 'Erreur',
          text: 'ID du bien invalide.',
          icon: 'error'
        });
        this.location.back();
        return;
      }

      this.loadSousBiens();
    });
  }

  loadSousBiens(): void {
    this.isLoading = true;
    this.sousBien.getBiensById(this.id).subscribe({
      next: (data: any) => {
        this.bienParent = data;
        const rawSousBiens = Array.isArray(data?.sous_biens) ? data.sous_biens : [];
        this.sousBiens = rawSousBiens;
        this.initializeOrderIndexes();
        this.isLoading = false;
        console.log('Sous-biens chargés:', this.sousBiens);
      },
      error: (err) => {
        console.error('Erreur chargement sous-biens:', err);
        this.isLoading = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Impossible de charger les sous-biens.',
          icon: 'error'
        });
      }
    });
  }

  private initializeOrderIndexes(): void {
    const hasOrder = this.sousBiens.some(s => s.displayOrder !== undefined && s.displayOrder !== null);
    if (hasOrder) {
      // Sort by displayOrder ascending
      this.sousBiens.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    } else {
      // Fallback to createdAt (oldest first)
      this.sousBiens.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      });
    }
    // Assign sequential orderIndex for UI
    this.sousBiens.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
  }

  // ============================================================
  // ORDERING METHODS (Batch update)
  // ============================================================

  private saveAllOrdersBatch(): void {
    if (this.sousBiens.length === 0) return;
    this.isLoading = true;
    const updates = this.sousBiens.map(bien => ({
      id: bien.id,
      displayOrder: bien.displayOrder!
    }));
    this.sousBien.updateSousBiensOrderBatch(updates).subscribe({
      next: () => {
        this.isLoading = false;
        Swal.fire({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 1500,
          icon: 'success',
          title: 'Ordre mis à jour'
        });
      },
      error: (err) => {
        console.error('Batch update error:', err);
        this.isLoading = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Impossible de sauvegarder l\'ordre. Rechargement...',
          icon: 'error'
        });
        this.loadSousBiens(); // reload to revert
      }
    });
  }

  private updateOrdersAndSave(): void {
    this.sousBiens.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
    this.saveAllOrdersBatch();
  }

  drop(event: CdkDragDrop<SousBien[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.sousBiens, event.previousIndex, event.currentIndex);
    this.updateOrdersAndSave();
  }

  moveUp(index: number): void {
    if (index === 0) return;
    [this.sousBiens[index], this.sousBiens[index - 1]] = [this.sousBiens[index - 1], this.sousBiens[index]];
    this.updateOrdersAndSave();
  }

  moveDown(index: number): void {
    if (index === this.sousBiens.length - 1) return;
    [this.sousBiens[index], this.sousBiens[index + 1]] = [this.sousBiens[index + 1], this.sousBiens[index]];
    this.updateOrdersAndSave();
  }

  // ============================================================
  // DELETE
  // ============================================================

  deletesousBiens(sousBien: SousBien): void {
    Swal.fire({
      title: 'Êtes-vous sûr(e) ?',
      text: 'Vous ne pourrez pas revenir en arrière !',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, supprimez-le !'
    }).then((result) => {
      if (result.isConfirmed) {
        this.sousBien.deleteSousBien(sousBien.id).subscribe({
          next: () => {
            Swal.fire({
              title: 'Supprimé !',
              text: 'Le sous-bien a été supprimé.',
              icon: 'success'
            });
            this.sousBiens = this.sousBiens.filter(s => s.id !== sousBien.id);
            this.updateOrdersAndSave(); // re-index after deletion
          },
          error: (err) => {
            console.error('Erreur suppression:', err);
            Swal.fire({
              title: 'Erreur',
              text: 'La suppression a échoué.',
              icon: 'error'
            });
          }
        });
      }
    });
  }

  navigateBack(): void {
    this.location.back();
  }
}